import { cva } from "class-variance-authority";
import { Beer, CreditCard, Monitor, UserCheck, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { TenantSwitcher, useTenant } from "@workspace/tenant-context";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import {
	CheckInOutPinOnly,
	useStaffPortalContext,
} from "@workspace/venue-management-staff";
import { LargeDigitalClock } from "@workspace/venue-management-staff-terminal/components";
import { useKioskContext } from "@workspace/venue-management-staff-terminal/kiosk";
import {
	deviceManagementApi,
	type Device,
} from "@workspace/venue-management/devices";

import { HelpSupportLink } from "@/components/help-support-link";
import { PageLoadingFull } from "@/components/page-loading";

const panelVariants = cva("h-38", {
	variants: {
		variant: {
			epos: "bg-linear-to-r from-blue-600 to-blue-800",
			employeeCentre: "bg-linear-to-r from-green-600 to-green-800",
		},
	},
	defaultVariants: {
		variant: "epos",
	},
});

export const Route = createFileRoute(
	"/_auth/_dashboard/$tenant/venues/$venueId/",
)({
	component: RouteComponent,
	pendingComponent: PageLoadingFull,
});

function RouteComponent() {
	const nav = Route.useNavigate();
	const { venueId, tenant } = Route.useRouteContext();
	const { allTenants: availableTenants, setTenant } = useTenant();

	const {
		handle,
		showCheckInOut,
		setShowCheckInOut,
		timeZone,
		deviceId,
		setDeviceId,
	} = useKioskContext();
	const { data, todayShift } = useStaffPortalContext();

	const devicesQuery = useQuery(
		deviceManagementApi.getDevicesOptions(tenant, venueId),
	);
	const selectedDevice = devicesQuery.data?.devices.find(
		(d: Device) => d.id === deviceId,
	);

	function handleEposClick() {
		nav({
			to: "/$tenant/venues/$venueId/epos",
			params: { tenant, venueId },
		});
	}

	function handleEmployeeCentreClick() {
		nav({
			to: "/$tenant/venues/$venueId/employee-centre",
			params: { tenant, venueId },
		});
	}

	if (showCheckInOut) {
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
				<CheckInOutPinOnly
					tenant={tenant}
					venueId={venueId}
					staffMembers={data.staffMembers}
					shifts={todayShift}
					onClose={() => setShowCheckInOut(false)}
					timeZone={timeZone}
				/>
			</div>
		);
	}

	return (
		<div className="flex min-h-(--kiosk-vh,100vh) flex-col p-2">
			<div className="flex min-h-full flex-col">
				<div className="flex justify-between gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setShowCheckInOut(true)}
						className="flex items-center gap-1 bg-primary-foreground"
					>
						<UserCheck className="size-4" />
						<span className="hidden sm:inline">Check In/Out</span>
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setDeviceId(null)}
						className="flex items-center gap-1 bg-primary-foreground"
					>
						<Monitor className="size-4" />
						<span className="hidden sm:inline">
							{selectedDevice ? selectedDevice.name : "No Device"}
						</span>
					</Button>
					{availableTenants.length > 2 && (
						<TenantSwitcher
							size="sm"
							setActiveTenant={async (t) => {
								if (t === tenant) return;

								await setTenant(t);

								await nav({
									to: "/app/$tenant",
									params: { tenant: t },
									reloadDocument: true,
								});
							}}
							tenant={tenant}
							tenants={[
								...availableTenants.map((t) => ({
									label: t.label,
									logo: Beer,
									plan: "Free",
									key: t.key,
									lastModified: t.lastModified,
									createdAt: t.createdAt,
								})),
							]}
						/>
					)}
					<HelpSupportLink />
					<>
						{!handle.active && (
							<Button variant="outline" size="sm" onClick={handle.enter}>
								Enter fullscreen
							</Button>
						)}
					</>

					<>
						{handle.active && (
							<Button variant="outline" size="sm" onClick={handle.exit}>
								Exit fullscreen
							</Button>
						)}
					</>
				</div>
				<main className="relative flex flex-1 flex-col items-center justify-center p-6">
					<div className="mb-4 grid size-full max-w-6xl grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-2">
						<Card
							className="flex-1 cursor-pointer overflow-hidden border-2 transition-shadow hover:shadow-lg"
							onClick={handleEposClick}
						>
							<div className={panelVariants({ variant: "epos" })}>
								<div className="flex h-full items-center justify-center">
									<CreditCard className="size-24 text-white opacity-50" />
								</div>
							</div>
							<CardContent className="p-6">
								<h2 className="mb-2 text-2xl font-bold">EPOS System</h2>
								<p className="hidden text-muted-foreground">
									Access the point of sale system to process orders and
									payments.
								</p>
								<Button className="mt-4 w-full" size="lg">
									Access EPOS
								</Button>
							</CardContent>
						</Card>

						<Card
							className="cursor-pointer overflow-hidden border-2 transition-shadow hover:shadow-lg"
							onClick={handleEmployeeCentreClick}
						>
							<div className={panelVariants({ variant: "employeeCentre" })}>
								<div className="flex h-full items-center justify-center">
									<Users className="size-24 text-white opacity-50" />
								</div>
							</div>
							<CardContent className="p-6">
								<h2 className="mb-2 text-2xl font-bold">Employee Centre</h2>
								<p className="hidden text-muted-foreground">
									Access your schedule, check in for shifts, view messages, and
									manage your profile.
								</p>
								<Button className="mt-4 w-full" size="lg" variant="outline">
									Access Employee Centre
								</Button>
							</CardContent>
						</Card>
					</div>

					{/* Large Clock Display */}
					<div className="mx-auto w-full max-w-3xl">
						<Card className="overflow-hidden border-2 bg-linear-to-r shadow-md">
							<CardContent className="flex flex-col items-center p-8">
								<LargeDigitalClock timeZone={timeZone} />
							</CardContent>
						</Card>
					</div>
				</main>
			</div>
		</div>
	);
}
